// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Layout Components
import Navigation from './components/layout/Navigation';
import Footer from './components/layout/Footer';
import ConnectionStatus from './components/ConnectionStatus';

// Public Pages
import LandingPage from './components/LandingPage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import ReviewsPage from './components/ReviewsPage';

// Private Pages
import Dashboard from './components/dashboard/Dashboard';
import TaskDashboard from './components/TaskDashboard';
import TaskDetail from './components/TaskDetail';
import NewTask from './components/NewTask';
import ImportTasks from './components/ImportTasks';
import Reports from './components/Reports';
import ProfileSettings from './components/profile/ProfileSettings';
import TaskEdit from './components/TaskEdit';

// Admin Pages
import UserManagement from './components/admin/UserManagement';
import AdminReviews from './components/admin/AdminReviews';
import AdminContacts from './components/admin/AdminContacts';
import SystemSettings from './components/admin/SystemSettings';

// Utility Components
import NotFound from './components/NotFound';
import Unauthorized from './components/Unauthorized';
import PrivateRoute from './components/PrivateRoute';

function App() {
  const { user } = useSelector(state => state.auth);

  return (
    <Router>
      <div className="app">
        <Routes>
          {/* Public Routes without Navigation */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email" element={user ? <VerifyEmail /> : <Navigate to="/login" replace />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />

          {/* Routes with Navigation */}
          <Route path="/*" element={
            <>
              {user && <Navigation />}
              <main className="main-content">
                {/*} <ConnectionStatus />*/}
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/tasks/:taskId" element={<PrivateRoute><TaskDetail /></PrivateRoute>} />
                  <Route path="/tasks/new" element={<PrivateRoute><NewTask /></PrivateRoute>} />
                  <Route path="/tasks/import" element={<PrivateRoute><ImportTasks /></PrivateRoute>} />
                  <Route path="/tasks/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
                  <Route path="/tasks/:taskId/edit" element={<PrivateRoute><TaskEdit /></PrivateRoute>} />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  } />
                  <Route path="/tasks" element={
                    <PrivateRoute>
                      <TaskDashboard />
                    </PrivateRoute>
                  } />
                  <Route path="/profile" element={
                    <PrivateRoute>
                      <ProfileSettings />
                    </PrivateRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="/admin/users" element={
                    <PrivateRoute requiredRoles={['admin', 'super_admin']}>
                      <UserManagement />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/reviews" element={
                    <PrivateRoute requiredRoles={['admin', 'super_admin']}>
                      <AdminReviews />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/contacts" element={
                    <PrivateRoute requiredRoles={['admin', 'super_admin']}>
                      <AdminContacts />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/settings" element={
                    <PrivateRoute requiredRoles={['super_admin']}>
                      <SystemSettings />
                    </PrivateRoute>
                  } />

                  {/* Not Found */}
                  <Route path="/*" element={<NotFound />} />
                </Routes>
              </main>
              {user && <Footer />}
            </>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;